// ConnectionTemplate authoring types (#5759, ADR-032). Mirror of the Domain
// `ConnectionTemplateContent` value object in its wire form: camelCase keys from the serializer's
// naming policy, enums as strings. Optional members are nullable so any stored document loads —
// structural completeness is a publish-time invariant, not a type constraint here.

export type AuthoringLevel = "Platform" | "Customer";
export type ConnectionTemplateStatus = "Draft" | "Published";

/** The closed `authFlow.kind` vocabulary — mirrors `AuthFlowKinds` (Application). */
export const AUTH_FLOW_KINDS = [
  "oauth2-authcode",
  "oauth2-clientcreds",
  "oauth2-password",
  "jwt-bearer",
  "vendor-initiated",
  "apikey",
  "basic",
  "bearer",
  "mtls-only",
  "none",
] as const;

/** The handler names an `authFlow.handlers` map may declare — mirrors `HandlerNames` (Application). */
export const HANDLER_NAMES = [
  "authorize",
  "token",
  "info",
  "refresh",
  "invalidate",
  "test",
] as const;

/** Protocols the platform can execute — grows when the engine grows (SFTP is designed, not shipped). */
export const TEMPLATE_PROTOCOLS = ["http"] as const;

/** The CredentialKind vocabulary a credentials-block entry may name — mirrors `CredentialKind` (Domain). */
export const CREDENTIAL_KINDS = ["X509", "SshKey", "PgpKey"] as const;

/** A BBEL-templated HTTP request shape used by a handler. */
export interface RequestSpec {
  url: string;
  method?: string | null;
  headers?: Record<string, string> | null;
  query?: Record<string, string> | null;
  body?: string | null;
  /** The body's media type — typed, never a `Content-Type` header entry (publish rejects those). */
  contentType?: string | null;
}

/**
 * One named handler: request shape + optional post-call write directives (`scope.field` → BBEL).
 * The reserved `connection.accessToken` / `refreshToken` / `expiresAt` targets carry engine token
 * semantics (ADR-022): required-fails-empty, kept-if-absent, and typed-expiry respectively.
 */
export interface TemplateHandlerSpec {
  request: RequestSpec;
  onResponse?: Record<string, string> | null;
}

/** Headers / query applied to every outbound call on a method once authenticated. */
export interface OutboundSpec {
  headers?: Record<string, string> | null;
  query?: Record<string, string> | null;
}

export interface FramePolicySpec {
  allowedFrameAncestors?: string[] | null;
}

/** Vendor-initiated inbound config (ADR-009 §4.8). */
export interface InboundSpec {
  queryShape?: Record<string, string> | null;
  framePolicy?: FramePolicySpec | null;
  returnUrlField?: string | null;
}

/** One declared connection field. `input` = user supplies at setup; `secret` = encrypted bag. */
export interface ConnectionFieldSpec {
  name: string;
  secret?: boolean;
  input?: boolean;
}

/** One platform-secret the method requires — names the variable; the value lives in the platform store. */
export interface PlatformVariableSpec {
  name: string;
  secret?: boolean;
  label?: string | null;
}

/**
 * One Credential this method requires the connection to link. `role` is the purpose slot (the key
 * BBEL reads as `credentials['role'].field` and LinkedCredentials stores); `kind` names the
 * acceptable CredentialKind (X509 / SshKey / PgpKey — validity is a publish-time invariant).
 */
export interface CredentialRequirementSpec {
  role: string;
  kind: string;
  label?: string | null;
  optional?: boolean;
}

export interface MtlsConfigSpec {
  credentialRole: string;
}

export interface NtlmConfigSpec {
  username: string;
  password: string;
  domain?: string | null;
}

/** Transport-level auth blocks layered under the auth flow (mTLS / NTLM). */
export interface TransportConfigSpec {
  mtls?: MtlsConfigSpec | null;
  ntlm?: NtlmConfigSpec | null;
}

/**
 * The auth flow for a method. `kind` is the closed engine-dispatch vocabulary; `handlers` is the
 * open keyed map of named steps (authorize / token / refresh / info / test).
 */
export interface TemplateAuthFlowSpec {
  kind: string;
  pkce?: string | null;
  handlers?: Record<string, TemplateHandlerSpec> | null;
  outbound?: OutboundSpec | null;
  inbound?: InboundSpec | null;
}

/**
 * HTTP protocol config for a method: base URL, auth flow, and HTTP transport tuning (mTLS / NTLM).
 * Present when the template's `protocol` is `http` — mirrors the Domain `HttpMethodConfig`.
 */
export interface HttpMethodConfigSpec {
  baseUrl?: string | null;
  authFlow?: TemplateAuthFlowSpec | null;
  transportConfig?: TransportConfigSpec | null;
}

/**
 * One selectable way to connect on a template. `id` (defaulting to the HTTP auth-flow kind when
 * omitted) is the selection identity a connection records; `connection` is this method's field
 * schema and `credentials` the credentials it links. Protocol-specific config lives under `http`
 * (SFTP authoring is a separate surface). Named `-Content` to stay distinct from the read-projection
 * `TemplateAuthMethod` in connection-types.
 */
export interface TemplateAuthMethodContent {
  id?: string | null;
  label?: string | null;
  connection?: ConnectionFieldSpec[] | null;
  http?: HttpMethodConfigSpec | null;
  platform?: PlatformVariableSpec[] | null;
  credentials?: CredentialRequirementSpec[] | null;
}

/**
 * How fast a vendor may be called, as they state it — mirrors the Domain `RateLimit`. Two numbers
 * because rounding "60 a minute" into a per-second figure loses everything below ten. Bounded server-side
 * between 1 per 60s and 100 a second, the fastest our own store will take.
 */
export interface RateLimitSpec {
  sends: number;
  perSeconds: number;
}

/** The slowest and fastest a declared rate may be — mirrors the Domain `RateLimit` constants. */
export const RATE_LIMIT_FASTEST_SENDS_PER_SECOND = 100;
export const RATE_LIMIT_SLOWEST_PER_SECONDS = 60;

/** The typed content of a ConnectionTemplate — the vendor's declarative auth/transport model. */
export interface ConnectionTemplateContent {
  vendor?: string | null;
  protocol?: string | null;
  description?: string | null;
  authMethods: TemplateAuthMethodContent[];
  /** The vendor's documented rate. Null means nothing is declared, and the platform ceiling applies. */
  rateLimit?: RateLimitSpec | null;
}

/**
 * One publish-blocking failure from the 400's TemplateValidationProblemDetails: `methodId` anchors
 * it to the owning auth method (the method's selection id — explicit id, or its flow kind).
 */
export interface TemplateValidationFailure {
  methodId: string;
  kind: string;
  message: string;
}

/** Authoring catalogue row (ConnectionTemplateAuthoringListItemDto) — every template, draft + published. */
export interface ConnectionTemplateAuthoringListItem {
  id: string;
  name: string;
  authoringLevel: AuthoringLevel;
  status: ConnectionTemplateStatus;
  protocol?: string | null;
  logoUrl?: string | null;
  description?: string | null;
}

/**
 * Full-fidelity authoring projection (ConnectionTemplateAuthoringDto) — the shape the template
 * designer binds to. Carries `{{ platform.* }}` references only; platform secret values never
 * appear here.
 */
export interface ConnectionTemplateAuthoring {
  id: string;
  name: string;
  authoringLevel: AuthoringLevel;
  orgId?: string | null;
  status: ConnectionTemplateStatus;
  logoUrl?: string | null;
  description?: string | null;
  content: ConnectionTemplateContent;
  createdAt: string;
  updatedAt?: string | null;
}

export interface CreateConnectionTemplateRequest {
  name: string;
  authoringLevel: AuthoringLevel;
  content: ConnectionTemplateContent;
  logoUrl?: string | null;
  description?: string | null;
  orgId?: string | null;
}

/** Replaces content + display metadata; the server reverts the template to Draft. */
export interface UpdateConnectionTemplateRequest {
  content: ConnectionTemplateContent;
  logoUrl?: string | null;
  description?: string | null;
}
